/**
 * The per-network EVM chain sets — THE single source of truth, and deliberately a LEAF module.
 *
 * ── WHY THIS FILE EXISTS SEPARATELY FROM `config/wagmi.ts` ───────────────────────────────────────
 * Two independent consumers need the same list of chains:
 *   1. `config/wagmi.ts`, which feeds it to `createConfig()` as `chains` + `transports`;
 *   2. `utils/recipientCodeGuard.ts`, whose #144 sweep must probe EVERY configured EVM chain — a
 *      chain present in wagmi but absent from the sweep is a silent hole in a fund-loss guard, with
 *      no other symptom.
 *
 * The guard cannot import the list from `wagmi.ts` for that purpose and have it be TESTABLE: any
 * test that loads `wagmi.ts` drags in `createConfig`, connectors (MetaMask / WalletConnect /
 * Coinbase) and transports, so every such test mocks `@/config/wagmi` wholesale — at which point an
 * assertion "the swept set equals the configured set" is comparing a source constant against a
 * constant written in the test file, and passes no matter what the real config says. That is exactly
 * the shape the previous drift guard had.
 *
 * So the list lives HERE, in a module with no side effects and no runtime dependency beyond chain
 * DEFINITIONS (`wagmi/chains` re-exports `viem/chains`, which is pure data). It is safe to import
 * from a vitest environment unmocked, which is what makes the drift assertion real.
 *
 * ── DO NOT WRITE A CHAIN LIST ANYWHERE ELSE ──────────────────────────────────────────────────────
 * `*_CHAINS_BY_KEY` below is the only place either network's chains are named. Everything else in
 * the repo — the wagmi `chains` tuples, the wagmi `transports` keys, and the guard's
 * `GUARDED_EVM_CHAIN_IDS` — is DERIVED from it. `chainSets.test.ts` pins those derivations. If you
 * are about to add a `[sepolia, baseSepolia, ...]` literal to another file, you are re-opening the
 * drift surface this module was extracted to close.
 *
 * ── ADDING OR REMOVING A CHAIN ───────────────────────────────────────────────────────────────────
 * Add the key to `GUARDED_CHAIN_KEYS`, then to BOTH `*_CHAINS_BY_KEY` maps (the `satisfies` makes a
 * missing or unknown key a compile error), then to both `*_CHAINS` tuples and to the `transports`
 * maps in `wagmi.ts` (`createConfig` makes a missing transport a compile error).
 */
import {
  sepolia,
  baseSepolia,
  optimismSepolia,
  arbitrumSepolia,
  mainnet,
  base,
  optimism,
  arbitrum,
  polygon,
  bsc,
  bscTestnet,
} from 'wagmi/chains'
import { defineChain } from 'viem'

/** Polygon Amoy testnet — not exported by `wagmi/chains`, so defined here. */
export const polygonAmoy = defineChain({
  id: 80002,
  name: 'Polygon Amoy',
  nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 },
  rpcUrls: {
    default: { http: ['https://rpc-amoy.polygon.technology'] },
  },
  blockExplorers: {
    default: { name: 'PolygonScan', url: 'https://amoy.polygonscan.com' },
  },
  testnet: true,
})

/**
 * Robinhood Chain (#174) — Arbitrum Orbit L2, chainId 4663, ETH gas. Not in `wagmi/chains`, so
 * defined here (same carve-out as `polygonAmoy`). MAINNET-ONLY: it appears in `MAINNET_CHAINS_BY_KEY`
 * and is EXCLUDED from `TESTNET_CHAINS_BY_KEY` (there is no Robinhood testnet), so on a testnet build
 * `GUARDED_EVM_CHAIN_IDS.robinhood` is `undefined` and the #144 recipient-code sweep never touches
 * 4663 — which is what keeps `chainSets.test.ts`'s "networks share no chain id" assertion green.
 */
export const robinhoodChain = defineChain({
  id: 4663,
  name: 'Robinhood',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: {
    default: { http: ['https://rpc.mainnet.chain.robinhood.com'] },
  },
  blockExplorers: {
    default: { name: 'Blockscout', url: 'https://robinhoodchain.blockscout.com' },
  },
})

/**
 * Parse's own chain names for the six configured EVM chains, in canonical order.
 *
 * These are the keys `recipientCodeGuard` indexes by (`GUARDED_EVM_CHAIN_IDS[destChain]`), so they
 * must stay in step with the EVM members of the `Chain` union in `@/types`.
 */
export const GUARDED_CHAIN_KEYS = ['ethereum', 'base', 'optimism', 'arbitrum', 'polygon', 'bnb', 'robinhood'] as const

export type GuardedChainKey = (typeof GUARDED_CHAIN_KEYS)[number]

/** Structural minimum this module derives from. `createConfig` type-checks the full `Chain` shape. */
type ChainLike = { readonly id: number }

/**
 * THE testnet list. `satisfies` makes a missing key AND an unknown key a compile error, so the two
 * networks cannot fall out of step with each other or with `GUARDED_CHAIN_KEYS`.
 *
 * `robinhood` is deliberately EXCLUDED (`Exclude<GuardedChainKey,'robinhood'>`) — it is mainnet-only
 * (#174) and has no testnet counterpart. `toChainIdMap` accepts a `Partial` map and emits only the
 * present keys, so this omission is intentional and type-checked, not an oversight.
 */
export const TESTNET_CHAINS_BY_KEY = {
  ethereum: sepolia,
  base: baseSepolia,
  optimism: optimismSepolia,
  arbitrum: arbitrumSepolia,
  polygon: polygonAmoy,
  bnb: bscTestnet,
} as const satisfies Record<Exclude<GuardedChainKey, 'robinhood'>, ChainLike>

/** THE mainnet list. Same contract as above — includes `robinhood` (mainnet-only). */
export const MAINNET_CHAINS_BY_KEY = {
  ethereum: mainnet,
  base: base,
  optimism: optimism,
  arbitrum: arbitrum,
  polygon: polygon,
  bnb: bsc,
  robinhood: robinhoodChain,
} as const satisfies Record<GuardedChainKey, ChainLike>

/**
 * Tuple projections for `createConfig`, which needs a non-empty tuple of LITERAL chain types to
 * build its chain-id union (a `Chain[]` widens the union and breaks the `transports` key check).
 * Every element is a reference into the map above — the chain objects are named exactly once.
 */
export const TESTNET_CHAINS = [
  TESTNET_CHAINS_BY_KEY.ethereum,
  TESTNET_CHAINS_BY_KEY.base,
  TESTNET_CHAINS_BY_KEY.optimism,
  TESTNET_CHAINS_BY_KEY.arbitrum,
  TESTNET_CHAINS_BY_KEY.polygon,
  TESTNET_CHAINS_BY_KEY.bnb,
] as const

export const MAINNET_CHAINS = [
  MAINNET_CHAINS_BY_KEY.ethereum,
  MAINNET_CHAINS_BY_KEY.base,
  MAINNET_CHAINS_BY_KEY.optimism,
  MAINNET_CHAINS_BY_KEY.arbitrum,
  MAINNET_CHAINS_BY_KEY.polygon,
  MAINNET_CHAINS_BY_KEY.bnb,
  MAINNET_CHAINS_BY_KEY.robinhood,
] as const

/**
 * Project a per-network chain map to `Parse chain name → chain id`, emitting ONLY the keys present in
 * `byKey`. `Partial` (not the full `Record`) because the testnet map omits mainnet-only chains
 * (`robinhood`, #174): on testnet `byKey['robinhood']` is `undefined` and the key is filtered out, so
 * `TESTNET_EVM_CHAIN_IDS.robinhood` is `undefined` and the recipient-code sweep never probes 4663.
 */
function toChainIdMap(byKey: Partial<Record<GuardedChainKey, ChainLike>>): Readonly<Record<string, number>> {
  return Object.freeze(
    Object.fromEntries(
      GUARDED_CHAIN_KEYS.filter((key) => byKey[key]).map((key) => [key, byKey[key]!.id]),
    ),
  ) as Readonly<Record<string, number>>
}

/** Parse chain name → testnet chain id. Derived; never hand-written. */
export const TESTNET_EVM_CHAIN_IDS = toChainIdMap(TESTNET_CHAINS_BY_KEY)

/** Parse chain name → mainnet chain id. Derived; never hand-written. */
export const MAINNET_EVM_CHAIN_IDS = toChainIdMap(MAINNET_CHAINS_BY_KEY)
